// src/components/watchDetail/PlayByPlay/PlayByPlay.js
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  TrendingUp,
  ArrowUpRight,
  BarChart2,
  Users,
  Clock,
  ChevronDown,
  AlertTriangle,
  TrendingDown,
  Activity,
  ArrowDownRight,
  Target,
} from "lucide-react";
import "./PlayByPlay.css";
import CryptoIconWrapper from "../../../cryptoicons/CryptoIconWrapper";

const PlayByPlay = ({ race }) => {
  const [autoScroll, setAutoScroll] = useState(true);
  const [showAllEvents, setShowAllEvents] = useState(false);

  const getEventIcon = (type, data) => {
    switch (type) {
      case "position_change":
        return data?.token1?.percentChange > 0 ? (
          <ArrowUpRight className="text-green-500" />
        ) : (
          <ArrowDownRight className="text-red-500" />
        );
      case "momentum_shift":
        return <Activity className="text-blue-500" />;
      case "volume_spike":
        return (
          <BarChart2
            className={`text-purple-500 ${data?.volumeIncrease > 100 ? "animate-pulse" : ""
              }`}
          />
        );
      case "milestone":
        return (
          <Target
            className={`${data?.percentChange > 0 ? "text-green-500" : "text-red-500"
              }`}
          />
        );
      case "support_level":
        return data?.type === "resistance" ? (
          <TrendingUp className="text-orange-500" />
        ) : (
          <TrendingDown className="text-blue-500" />
        );
      case "new_participant":
        return <Users className="text-blue-500" />;
      default:
        return <AlertTriangle className="text-gray-500" />;
    }
  };

  const formatValue = (value, type = "number") => {
    // Check if value is undefined or null
    if (value == null) return "0";

    // Convert to number if it's a string
    const numValue = Number(value);

    // Check if conversion was successful
    if (isNaN(numValue)) return "0";

    if (type === "price") {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numValue);
    }
    if (type === "percent") {
      return `${numValue > 0 ? "+" : ""}${numValue.toFixed(2)}%`;
    }
    return numValue.toLocaleString();
  };

  const getEventDetails = (event) => {
    switch (event.type) {
      case "position_change":
        return (
          <div className="event-details">
            <span
              className={`change-value ${event.data.token1.percentChange > 0
                ? "text-green-500"
                : "text-red-500"
                }`}
            >
              {formatValue(event.data.token1.percentChange, "percent")}
            </span>
            <span className="text-gray-400">
              Position: {event.data.token1.oldPosition} →{" "}
              {event.data.token1.position}
            </span>
          </div>
        );
      case "momentum_shift":
        return (
          <div className="event-details">
            <span className="text-blue-500">
              Price: {formatValue(event.data.price, "price")}
            </span>
            <span
              className={`change-value ${event.data.percentChange > 0 ? "text-green-500" : "text-red-500"
                }`}
            >
              {formatValue(event.data.percentChange, "percent")}
            </span>
          </div>
        );
      case "volume_spike":
        return (
          <div className="event-details">
            <span className="text-purple-500">
              Volume: {formatValue(event.data.volumeUSD, "price")}
            </span>
          </div>
        );
      case "support_level":
        return (
          <div className="event-details">
            <span
              className={`level-value ${event.data.type === "resistance"
                ? "text-orange-500"
                : "text-blue-500"
                }`}
            >
              {event.data.type.charAt(0).toUpperCase() +
                event.data.type.slice(1)}
              : {formatValue(event.data.level, "price")}
            </span>
            <span className="text-gray-400">
              Current: {formatValue(event.data.price, "price")}
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  const events = race.playByPlay || [];
  const displayedEvents = showAllEvents ? events : events.slice(0, 10);

  if (!events.length) {
    return (
      <div className="play-by-play-empty">
        <AlertTriangle size={24} />
        <p>No race events yet</p>
      </div>
    );
  }

  return (
    <div className="play-by-play-container">
      <div className="play-by-play-header">
        <div className="header-title">
          <Clock size={20} />
          <h3>Market Analysis & Race Updates</h3>
          <div className="auto-scroll">
            <label className="auto-scroll-label">
              <input
                type="checkbox"
                checked={autoScroll}
                onChange={(e) => setAutoScroll(e.target.checked)}
              />
              <span>Auto-scroll</span>
            </label>
          </div>
        </div>
      </div>

      <div className={`events-list ${autoScroll ? "auto-scroll-enabled" : ""}`}>
        {displayedEvents.map((event, index) => (
          <div
            key={`${event.timestamp}-${index}`}
            className={`event-item ${event.importance}`}
          >
            <div className="event-icon">
              {getEventIcon(event.type, event.data)}
            </div>

            <div className="event-content">
              <div className="event-tokens">
                {event.tokens?.map((token) => (
                  <div key={token} className="token-badge">
                    <CryptoIconWrapper name={token.toLowerCase()} size={16} />
                    <span>{token}</span>
                  </div>
                ))}
              </div>

              <p className="event-description">{event.description}</p>
              {getEventDetails(event)}

              <div className="event-metadata">
                <span className="event-time">{event.formattedTime}</span>
                {event.importance === "high" && (
                  <span className="importance-badge">High Impact</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {events.length > 10 && (
        <button
          className="show-more-button"
          onClick={() => setShowAllEvents(!showAllEvents)}
        >
          <span>
            {showAllEvents
              ? "Show Less"
              : `Show ${events.length - 10} More Events`}
          </span>
          <ChevronDown
            size={20}
            className={`chevron ${showAllEvents ? "rotate-180" : ""}`}
          />
        </button>
      )}
    </div>
  );
};

PlayByPlay.propTypes = {
  race: PropTypes.shape({
    playByPlay: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        timestamp: PropTypes.number.isRequired,
        formattedTime: PropTypes.string.isRequired,
        tokens: PropTypes.arrayOf(PropTypes.string),
        description: PropTypes.string.isRequired,
        importance: PropTypes.string.isRequired,
        data: PropTypes.object.isRequired,
      })
    ).isRequired,
  }).isRequired,
};

export default PlayByPlay;
